import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useTranslation } from 'react-i18next';

import getTheme from './theme';
import LandingPage from './LandingPage';
import NizroPage from './pages/NizroPage';
import FawtaraPage from './pages/FawtaraPage';
import ContactPage from './pages/ContactPage';

// One cache per direction; swapped live when the language changes so MUI
// styles genuinely flip for Arabic instead of only the document dir.
const cacheLtr = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const cacheRtl = createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] });

export default function App() {
  const { i18n } = useTranslation();
  const dir = i18n.dir();
  const theme = React.useMemo(() => getTheme(dir), [dir]);

  return (
    <CacheProvider value={dir === 'rtl' ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* Every page twice: English at the bare path, Arabic under /ar. The
            language is the URL, which is the only way a crawler can see the
            Arabic at all and the only way hreflang means anything. */}
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/nizro" element={<NizroPage />} />
          <Route path="/fawtara" element={<FawtaraPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/ar" element={<LandingPage />} />
          <Route path="/ar/nizro" element={<NizroPage />} />
          <Route path="/ar/fawtara" element={<FawtaraPage />} />
          <Route path="/ar/contact" element={<ContactPage />} />
          {/* Any unmatched path (typo, stale bookmark, bad link) falls back
              to the home page rather than rendering blank — matches the
              pre-routing behaviour where every URL showed the one-page site. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  );
}
